import { cookies } from './cookies';

/**
 * Cookie names and formats must stay identical to the Webflow site, both sites write to the same `.productive.io` cookies.
 */
export const COOKIE_UTM = 'productive-utm';
export const COOKIE_UTM_VISITS = 'productive-utm-visits';
export const COOKIE_REF = 'productive-ref';
export const COOKIE_REF_DOMAIN = 'productive-ref-domain';
export const COOKIE_LANDING_PAGE = 'productive-landing-page';
export const COOKIE_ANALYTICS_UID = 'productive-analytics-uid';

// Last touch in the raw JSON format used by Webflow, written on every UTM hit and never trimmed like the visits.
const STORAGE_UTM_LAST = 'productive-utm-last';

export const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

// Short keys used for UTM values in stored visits.
const VISIT_UTM_KEYS = { source: 's', medium: 'm', campaign: 'cp', content: 'cn', term: 'tr' };

// Long UTM values would push a single visit over the cookie size limit.
const MAX_UTM_LENGTH = 250;

/**
 * Normalize a UTM object so it always contains every key, with values capped to `MAX_UTM_LENGTH`.
 *
 * @param {object} utm UTM object.
 *
 * @returns {object} UTM object with all keys.
 */
export function normalizeUtm(utm) {
	return Object.fromEntries(UTM_KEYS.map((key) => [key, typeof utm[key] === 'string' ? utm[key].slice(0, MAX_UTM_LENGTH) : '']));
}

/**
 * Get visits stored in the cookie.
 *
 * @returns {Array} Stored visits or an empty array if missing or invalid.
 */
export function getVisits() {
	const value = cookies.getCookie(COOKIE_UTM_VISITS);

	if (!value) {
		return [];
	}

	try {
		const visits = JSON.parse(decodeURIComponent(value));

		return Array.isArray(visits) ? visits.filter((visit) => visit && typeof visit === 'object') : [];
	} catch {
		return [];
	}
}

/**
 * Convert a UTM object to the short keys used in stored visits.
 *
 * @param {object} utm UTM object.
 *
 * @returns {object} UTM values with visit keys.
 */
export function utmToVisit(utm) {
	return Object.fromEntries(UTM_KEYS.map((key) => [VISIT_UTM_KEYS[key], utm[key]]));
}

/**
 * Get the UTM values of the newest stored visit that carried UTM, which is the last touch.
 *
 * @param {Array} visits Visits currently stored in the cookie, oldest first.
 *
 * @returns {object|null} UTM object or null if no visit carried UTM.
 */
export function getLastUtmVisit(visits) {
	const lastUtmVisit = [...visits].reverse().find((visit) => UTM_KEYS.some((key) => visit[VISIT_UTM_KEYS[key]]));

	if (!lastUtmVisit) {
		return null;
	}

	return normalizeUtm(Object.fromEntries(UTM_KEYS.map((key) => [key, lastUtmVisit[VISIT_UTM_KEYS[key]]])));
}

/**
 * Get the last touch UTM stored in localStorage.
 *
 * @returns {object|null} UTM object or null if missing, invalid or localStorage is blocked.
 */
export function getStoredLastUtm() {
	try {
		const utm = parseUtmCookie(localStorage.getItem(STORAGE_UTM_LAST));

		return utm && UTM_KEYS.some((key) => utm[key]) ? normalizeUtm(utm) : null;
	} catch {
		return null;
	}
}

/**
 * Store the last touch UTM in localStorage.
 *
 * @param {object} utm UTM object.
 *
 * @returns {void}
 */
export function setStoredLastUtm(utm) {
	try {
		localStorage.setItem(STORAGE_UTM_LAST, JSON.stringify(normalizeUtm(utm)));
	} catch {
		// localStorage blocked by the browser.
	}
}

/**
 * Parse a UTM cookie value.
 *
 * Supports the URL-encoded format (Webflow and current WP) and the legacy raw JSON format written by the old WP tracking.
 *
 * @param {string|null} value Raw cookie value.
 *
 * @returns {object|null} UTM object or null if missing or invalid.
 */
export function parseUtmCookie(value) {
	if (!value) {
		return null;
	}

	try {
		const utm = JSON.parse(isLegacyUtmCookie(value) ? value : decodeURIComponent(value));

		return utm && typeof utm === 'object' && !Array.isArray(utm) ? utm : null;
	} catch {
		return null;
	}
}

/**
 * Get the analytics UID, creating and storing it if it doesn't exist yet.
 *
 * @returns {string} 16 character hex UID.
 */
export function getAnalyticsUid() {
	const existingUid = cookies.getCookie(COOKIE_ANALYTICS_UID);

	if (existingUid) {
		return existingUid;
	}

	const uid = Array.from(crypto.getRandomValues(new Uint8Array(8)))
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('');

	cookies.setCookie(COOKIE_ANALYTICS_UID, uid, cookies.DURATION_ONE_YEAR);

	return uid;
}

/**
 * Check if the cookie value is the legacy raw JSON format.
 *
 * TODO: Remove after 1 November 2027, together with the legacy branch in `parseUtmCookie`. Legacy WP cookies have expired by then.
 *
 * @param {string|null} value Raw cookie value.
 *
 * @returns {boolean} True if legacy format.
 */
export function isLegacyUtmCookie(value) {
	return typeof value === 'string' && value.startsWith('{');
}
